import { Injectable } from '@angular/core';
import {
  ZonedDateTime,
  Instant,
  ZoneId,
  LocalDate,
  LocalDateTime,
  DateTimeFormatter
} from '@js-joda/core';
import '@js-joda/timezone';

const ISO_OFFSET = DateTimeFormatter.ISO_OFFSET_DATE_TIME;

type InputDate = string | Date | number;

@Injectable({ providedIn: 'root' })
export class TimezoneService {
  private userTz = this.resolveTimeZone();

  getUserTimeZone(): string {
    return this.userTz;
  }

  // -------- UTC → USER --------

  utcToUserTime(value: InputDate, tz: string = this.userTz): string {
    return this.formatInZone(value, tz);
  }

  utcToTimezone(value: InputDate, timezone: string): string {
    return this.formatInZone(value, timezone);
  }

  // -------- USER → UTC --------

  userToUtcTime(value: InputDate, tz: string = this.userTz): string {
    return this.toUtc(value, tz);
  }

  timezoneToUtc(value: InputDate, timezone: string): string {
    return this.toUtc(value, timezone);
  }

  // -------- DATE RANGE HELPERS --------

  /**
   * Single date → full UTC day range
   */
  toUtcDateRange(date: string, tz: string = this.userTz) {
    const zone = ZoneId.of(tz);

    const start = LocalDate.parse(date)
      .atStartOfDay()
      .atZone(zone)
      .toInstant()
      .toString();

    const end = LocalDate.parse(date)
      .atTime(23, 59, 59)
      .atZone(zone)
      .toInstant()
      .toString();

    return { start, end };
  }

  /**
   * BETWEEN filter → two dates
   */
  toUtcBetweenDates(fromDate: string, toDate: string, tz: string = this.userTz) {
    const zone = ZoneId.of(tz);

    const start = LocalDate.parse(fromDate)
      .atStartOfDay()
      .atZone(zone)
      .toInstant()
      .toString();

    const end = LocalDate.parse(toDate)
      .atTime(23, 59, 59)
      .atZone(zone)
      .toInstant()
      .toString();

    return { start, end };
  }


  toLocalDateInZone(value: InputDate, tz: string): LocalDate {
    const instant = this.toInstant(value);
    return ZonedDateTime.ofInstant(instant, ZoneId.of(tz)).toLocalDate();
  }
  

  private formatInZone(value: InputDate, timezone: string): string {
    const instant = this.toInstant(value);
    const zdt = ZonedDateTime.ofInstant(instant, ZoneId.of(timezone));
    return `${zdt.format(ISO_OFFSET)} [${timezone}]`;
  }

  private toUtc(value: InputDate, timezone: string): string {
    if (value instanceof Date || typeof value === 'number') {
      return this.toInstant(value).toString();
    }

    const str = value as string;

    if (this.isOffsetString(str)) {
      return this.toInstant(str).toString();
    }

    const ldt = LocalDateTime.parse(str);
    return ldt.atZone(ZoneId.of(timezone)).toInstant().toString();
  }

  private toInstant(value: InputDate): Instant {
    if (value instanceof Date) return Instant.ofEpochMilli(value.getTime());
    if (typeof value === 'number') return Instant.ofEpochMilli(value);
    return Instant.ofEpochMilli(new Date(value).getTime());
  }


  private isOffsetString(value: string): boolean {
    if (isNaN(new Date(value).getTime())) return false;
    return /Z$/i.test(value) || /[+\-]\d{2}:?\d{2}$/.test(value);
  }

  private resolveTimeZone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
      return 'UTC';
    }
  }
}